import React from 'react';
import { BUTTON_STYLES } from 'constants/styles';
import { ValidationWarning } from 'components/mealplan/StatusMessages';
import { sortFlavorGroups, sortProteinOptions } from 'utils/proteinOrdering';
import { getProteinUpgradeAmount, isProteinUpgradeChargeable } from 'utils/proteinPricing';
import SubstitutionBadge from './SubstitutionBadge';

const ProteinSubstitutionSection = ({
    order,
    orderIndex,
    onProteinSubstitution,
    isLocked,
    saving,
    billingChanges,
    deliveryDates,
    selectedDeliveryDate,
    autoSubstitution,
    // Storefront (MenuPage) passes true — badge shows the a la carte upgrade
    // price instead of the meal-plan subscription's flat premium surcharge.
    useALaCartePricing = false,
    // Servings of this dish already counted in the week's premium totals inside
    // `billingChanges` (0 for a draft — see MealModal).
    countedQuantity = 0,
}) => {
    const proteinData = order.proteinOptions;

    if (!proteinData) {
        return null;
    }

    const showAutoSubBadge = autoSubstitution?.addedId &&
        proteinData.some(opt => opt.id === autoSubstitution.addedId && opt.isActive);

    // Whether to show premium pricing on a given option, based on pre-calculated billing data
    const shouldShowPremiumPricing = (proteinOption) =>
        isProteinUpgradeChargeable({
            proteinOption,
            proteinOptions: proteinData,
            mealType: order.meal,
            billingChanges,
            deliveryDates,
            selectedDeliveryDate,
            useALaCarte: useALaCartePricing,
            countedQuantity,
        });

    const getActiveProteinCount = () => {
        if (!proteinData) return null;
        return proteinData.filter(option => option.isActive).length;
    };

    // Group proteins by flavor type
    const groupedByFlavor = {};
    proteinData.forEach(option => {
        const flavor = option.flavorType || 'Other';
        if (!groupedByFlavor[flavor]) groupedByFlavor[flavor] = [];
        groupedByFlavor[flavor].push(option);
    });
    // Flavored groups first, then Grilled, then Unseasoned; within each group
    // proteins run meat → fish → vegan → eggs (per Claire, Jul 2026).
    const flavorGroups = sortFlavorGroups(Object.entries(groupedByFlavor));
    const hasMultipleGroups = flavorGroups.length > 1;
    const ungroupedProteins = sortProteinOptions(proteinData);

    const renderButton = (proteinOption, index) => {
        const isActive = proteinOption.isActive;
        const isDisabled = isLocked || saving || isActive;
        const showPrice = shouldShowPremiumPricing(proteinOption);
        const upgradeAmount = getProteinUpgradeAmount(proteinOption, useALaCartePricing);
        const label = proteinOption.variantName || proteinOption.name;
        const subs = (proteinOption.subIngredientNames || []).map(s => (s || '').replace(/^SF /, ''));
        const title = subs.length > 0
            ? `${label}: ${subs.join(', ')}`
            : (isActive ? `Current protein: ${label}` : `Switch to ${label}`);
        return (
            // Tooltip lives on this wrapper, not the button — Chromium won't
            // show a title tooltip on a disabled button, and the currently
            // active option (the one you'd most want to inspect) is disabled.
            <span key={index} title={title}>
                <button
                    onClick={() => {
                        if (!isActive && onProteinSubstitution) {
                            const currentActiveOption = proteinData.find(opt => opt.isActive);
                            const oldProteinId = currentActiveOption ? currentActiveOption.id : null;
                            onProteinSubstitution(orderIndex, proteinOption.id, oldProteinId);
                        }
                    }}
                    disabled={isDisabled}
                    style={BUTTON_STYLES.substitution(isActive, isDisabled)}
                    data-testid={`protein-option-${proteinOption.id}`}
                >
                    <span style={{
                        marginRight: '4px',
                        fontSize: '12px'
                    }}>
                        {isActive ? '✓' : '+'}
                    </span>
                    {proteinOption.variantName || proteinOption.name}
                    {showPrice && (
                        <span style={{
                            marginLeft: '4px',
                            fontSize: '11px',
                            fontWeight: 'bold',
                            color: '#856404'
                        }}>
                            (+${upgradeAmount})
                        </span>
                    )}
                </button>
            </span>
        );
    };

    return (
        <div style={{ marginBottom: '15px' }}>
            <label style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '14px',
                fontWeight: 'bold',
                color: '#555'
            }}>
                Protein:
                {showAutoSubBadge && (
                    <SubstitutionBadge
                        removedName={autoSubstitution.removedName}
                        addedName={autoSubstitution.addedName}
                    />
                )}
            </label>
            {getActiveProteinCount() !== null && getActiveProteinCount() !== undefined && getActiveProteinCount() < 1 && (
                <ValidationWarning message="No Protein Selected" />
            )}
            {hasMultipleGroups ? (
                flavorGroups.map(([flavor, options]) => (
                    <div key={flavor} style={{ marginBottom: '10px' }}>
                        <div style={{
                            fontSize: '12px',
                            fontWeight: '600',
                            color: '#888',
                            marginBottom: '4px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                        }}>
                            {flavor}
                            {flavor === 'Grilled' && (
                                <span style={{
                                    fontWeight: '400',
                                    textTransform: 'none',
                                    letterSpacing: 'normal',
                                    marginLeft: '4px'
                                }}>
                                    (olive oil, salt, pepper)
                                </span>
                            )}
                        </div>
                        <div style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '8px'
                        }}>
                            {options.map((option, idx) => renderButton(option, `${flavor}-${idx}`))}
                        </div>
                    </div>
                ))
            ) : (
                <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '8px'
                }}>
                    {ungroupedProteins.map((option, idx) => renderButton(option, idx))}
                </div>
            )}
        </div>
    );
};

export default ProteinSubstitutionSection;